import { useCallback, useMemo, useState } from "react";
import type { Dp, Filter } from "./ExState";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import { IconButton, Menu, MenuItem, Checkbox } from "@mui/material";
import OpenInFull from "@mui/icons-material/OpenInFull";
import AugmentImage from "common/src/tft-static/AugmentImage";
import { make as AugmentName } from "common/src/tft-static/AugmentName.bs";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import { make as SearchField } from "../components/SearchField2.bs";
import { searchAugments } from "../common/Search2";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { make as AugmentTooltip } from "common/src/tft-static/AugmentTooltip.bs";
import { common as t } from "common/src/bindings/Translation2";
import { use as useSet } from "common/src/data/TftSet.bs";
import { augments } from "common/src/data/Data.bs";

const cols = [
  makeCol({
    width: 170,
    order: ([augmentId], _, { augmentsT }) => augmentsT(augmentId),
    defaultDesc: false,
    renderTitle: (_, { t }) => <> {t("Augment")} </>,
    renderValue: ([augId, _]) => (
      <div className="flex items-center text-sm">
        <div className="flex-shrink-0">
          <AugmentTooltip id={augId}>
            <AugmentImage augId={augId} size={28} />
          </AugmentTooltip>
        </div>
        <div className="pl-[6px] font-roboto font-normal truncate">
          <AugmentName id={augId} />
        </div>
      </div>
    ),
  }),
  makeCol({
    width: 80,
    order: ([_, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Games")} </>,
    renderValue: ([_, v]) => <> {humanReadableCount(v.count)} </>,
  }),
  makeCol({
    width: 70,
    order: ([_, v]) => v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Place")} </>,
    renderValue: ([_, v]) => <> {(v.place / v.count).toFixed(2)} </>,
  }),
  makeCol({
    width: 82,
    order: ([_, v]) => v.top4 / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Top 4")}% </>,
    renderValue: ([_, v]) => <> {formatPct2(v.top4 / v.count)} </>,
  }),
  makeCol({
    width: 72,
    order: ([_, v]) => v.won / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Win")}% </>,
    renderValue: ([_, v]) => <> {formatPct2(v.won / v.count)} </>,
  }),
  makeCol({
    width: 72,
    order: ([_1, _2, v]) => v && v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("At 2-1")}</>,
    renderValue: ([, , v]) => {
      if (v && v.place / v.count < 7.5) {
        return (
          <Tooltip
            title={`${humanReadableCount(v.count)} games and ${(
              v.place / v.count
            ).toFixed(2)} avg. place when picked as 1st augment (stage 2-1)`}
          >
            <div> {(v.place / v.count).toFixed(2)} </div>
          </Tooltip>
        );
        // return <> {humanReadableCount(v.count)} </>;
      } else {
        return <>—</>;
      }
    },
  }),
  makeCol({
    width: 72,
    order: ([_1, _2, _3, v]) => v && v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <>{t("At 3-2")}</>,
    renderValue: ([_1, _2, _3, v]) => {
      if (v) {
        return (
          <Tooltip
            title={`${humanReadableCount(v.count)} games and ${(
              v.place / v.count
            ).toFixed(2)} avg. place when picked as 2nd augment (stage 3-2)`}
          >
            <div> {(v.place / v.count).toFixed(2)} </div>
          </Tooltip>
        );
        // return <> {humanReadableCount(v.count)} </>;
      } else {
        return <> — </>;
      }
    },
  }),
  makeCol({
    width: 72,
    order: ([_1, _2, _3, _4, v]) => v && v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <>{t("At 4-2")}</>,
    renderValue: ([_1, _2, _3, _4, v]) => {
      if (v) {
        return (
          <Tooltip
            title={`${humanReadableCount(v.count)} games and ${(
              v.place / v.count
            ).toFixed(2)} avg. place when picked as 3rd augment (stage 4-2)`}
          >
            <div> {(v.place / v.count).toFixed(2)} </div>
          </Tooltip>
        );
        // return <> {humanReadableCount(v.count)} </>;
      } else {
        return <>—</>;
      }
    },
  }),
  makeCol({
    width: 48,
    alignRight: true,
    renderTitle: (_) => <div className="h-1" />,
    valueClass: "!justify-start",
    renderValue: ([augId], { onExpand, hideExpand }, { augmentsT }) => {
      return hideExpand ? (
        <div />
      ) : (
        <IconButton
          onClick={(_) =>
            onExpand({
              typ: "a",
              id: augId,
              sid: String(augId),
              pick: 0,
              label: augmentsT(augId),
            })
          }
          classes={{
            root: "text-lg",
          }}
        >
          <OpenInFull fontSize="inherit" />
        </IconButton>
      );
    },
  }),
];

type Props = {
  data: Array<[number, Dp, Dp | null, Dp | null, Dp | null]>;
  baseCount: number;
  onExpand: (f: Filter) => void;
  hideExpand: boolean;
  minSample: number;
};

const ExAugments = ({
  data,
  baseCount,
  onExpand,
  hideExpand,
  minSample,
}: Props) => {
  const set = useSet();
  const [search, setSearch] = useState("");
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [showSilvers, setShowSilvers] = useState(false);
  const [showGolds, setShowGolds] = useState(false);
  const [showPrisms, setShowPrisms] = useState(false);
  const filtersEnabled =
    !(showSilvers && showGolds && showPrisms) &&
    (showSilvers || showGolds || showPrisms);
  const keyFn = useCallback(([id, _]: [number, Dp]) => String(id), []);
  const context = useMemo(
    () => ({ baseCount, onExpand, hideExpand }),
    [baseCount, onExpand, hideExpand],
  );

  const filteredData = useMemo(
    () => data.filter(([_, v]) => v.count >= minSample),
    [data, minSample],
  );

  const visibleFilter = useMemo(() => {
    const showAllTiers =
      (showSilvers && showGolds && showPrisms) ||
      (!showSilvers && !showGolds && !showPrisms);

    let passSearch: any;
    if (search === "") {
      passSearch = () => true;
    } else {
      const passIds = searchAugments(search, set);
      passSearch = (augId: string) => passIds.has(augId);
    }

    let passTier: any;
    if (showAllTiers) {
      passTier = () => true;
    } else {
      passTier = (augId: string) => {
        const info = augments(set)[augId];
        if (info.rank === 1) {
          return showSilvers;
        } else if (info.rank === 2) {
          return showGolds;
        } else if (info.rank === 3) {
          return showPrisms;
        } else {
          return false;
        }
      };
    }

    return ([augId]: [string]) => passTier(augId) && passSearch(augId);
  }, [search, showSilvers, showGolds, showPrisms]);

  if (data.length === 0) {
    return (
      <div className="px-5 py-6 text-xl text-medium text-white2">
        No augment combos with at least 100 games played
      </div>
    );
  } else {
    return (
      <>
        <div className="p-2 flex justify-between">
          <SearchField search={search} setSearch={setSearch} />
          <div
            className="mr-4 cursor-pointer"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <FilterAltIcon color={filtersEnabled ? "primary" : undefined} />
          </div>
        </div>
        <div>
          <StatsTable
            cols={cols}
            context={context}
            data={filteredData}
            keyFn={keyFn}
            stickyColCount={1}
            defaultSortIdx={1}
            top="t47m92"
            visibleFilter={visibleFilter}
          />
        </div>
        <Menu
          open={menuAnchor !== null}
          anchorEl={menuAnchor}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => setShowSilvers((v) => !v)}
            classes={{
              root: "pl-1 py-[2px]",
            }}
          >
            <Checkbox size="small" checked={showSilvers} />
            {t("Silver")}
          </MenuItem>
          <MenuItem
            onClick={() => setShowGolds((v) => !v)}
            classes={{
              root: "pl-1 py-[2px]",
            }}
          >
            <Checkbox size="small" checked={showGolds} />
            {t("Gold")}
          </MenuItem>
          <MenuItem
            onClick={() => setShowPrisms((v) => !v)}
            classes={{
              root: "pl-1 py-[2px]",
            }}
          >
            <Checkbox size="small" checked={showPrisms} />
            {t("Prismatic")}
          </MenuItem>
        </Menu>
      </>
    );
  }
};

export default ExAugments;
